import { useState, useEffect, useCallback, useRef } from 'react';
import { FormattedMessage, defineMessages } from '@alltrails/shared/react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import Button from '@alltrails/shared/denali/components/Button';
import useActivityStrings from '@alltrails/shared/hooks/useActivityStrings';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import { usersContentPrivacyPolicy, userCanEdit } from 'utils/UserUtils';
import TrailCard from 'components/cards/TrailCard';
import useUser from 'hooks/useUser';
import { Context } from '@alltrails/shared/types/context';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { VISIBILITY_PUBLIC } from 'utils/privacy_policy_helpers';
import { useRoutes, CANCEL_ROUTE_FORM, UPDATE_FOR_INCOMING_ROUTES } from 'hooks/useRoutes';
import { useWaypoints, UPDATE_FOR_INCOMING_WAYPOINTS, CANCEL_WAYPOINT_FORM } from 'hooks/useWaypoints';
import { routeRequest, waypointRequest, formatWaypointForMap } from 'utils/map_creator_util';
import Expandable from '@alltrails/shared/components/Expandable';
import PortalModal from 'components/shared/PortalModal';
import { modalRoadblock } from '@alltrails/shared/utils/modalFunnelUtils';
import CreatedMapModal from '../CreatedMapModal';
import RouteSection from '../Routing/RouteSection';
import WaypointsSection from '../../Waypoints/WaypointsSection';
import DescriptionForm from '../DescriptionForm';
import ActionHeader from '../../shared/ActionHeader';
import EditHeader from '../EditHeader';
import { deleteMap } from '../../../utils/requests/at_map_requests';
import PhotoSection from '../Photos';
import DownloadRouteModal from '../../DownloadRouteModal';
import { logIndividualContentPrivacyChangedAmplitudeEvent } from '../../../utils/privacy_policy_amplitude_helpers';
import * as styles from './styles/styles.module.scss';

const PANEL_STRINGS = defineMessages({
  EDIT_MAP: { defaultMessage: 'Edit map' },
  DESCRIPTION: { defaultMessage: 'Description' },
  WAYPOINTS: { defaultMessage: 'Waypoints' },
  PHOTOS: { defaultMessage: 'Photos' },
  SAVE_CUSTOM_MAP: { defaultMessage: 'Save custom map' },
  DELETE_ITEM_QUESTION: { defaultMessage: 'Are you sure you want to delete this from AllTrails?' }
});

type Props = {
  context: Context;
  map?: any;
  openTrailFormModal?: any;
  messagingChannel?: any;
  mapboxAccessToken?: any;
  handleMapUpdate?: any;
  resultCardFunctions?: any;
  updateMapPhotos: any;
  openShareModal: any;
  linkedTrail: any;
  initialIsEditing: any;
  updateExploreMap: any;
  saveMapChanges: any;
  routeInfo: any;
  updateProgressDialog: any;
  showMapMobile: any;
  hidePanel: any;
  listMethods: any;
  handleRouteInfoChanged: any;
  saveMapAsClone: any;
};

const MapCreatorPanel = ({
  map,
  messagingChannel,
  mapboxAccessToken,
  handleMapUpdate,
  openTrailFormModal,
  updateMapPhotos,
  openShareModal,
  resultCardFunctions,
  linkedTrail,
  initialIsEditing,
  updateExploreMap,
  saveMapChanges,
  routeInfo,
  updateProgressDialog,
  showMapMobile,
  hidePanel,
  listMethods,
  handleRouteInfoChanged,
  saveMapAsClone
}: Props) => {
  const {
    formattedDefaultMessages: { EDIT_MAP, DESCRIPTION, WAYPOINTS, PHOTOS, SAVE_CUSTOM_MAP, DELETE_ITEM_QUESTION }
  } = useFormatMessage(PANEL_STRINGS);

  // editingMethods
  let saveMap: any;
  let handleRouteSuccess: any;
  let handleRouteSave;
  let saveAndDisplayMap: (e: React.FormEvent<HTMLFormElement> | MouseEvent, fromSubForm?: boolean) => void;
  let createMapWithWaypoint: any;
  let handleSuccessfulMapSave: any;
  let handleMapError: any;

  const { findByValue } = useActivityStrings();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const canEdit = userCanEdit(user, map.user);
  const [isEditing, toggleEditing] = useState(initialIsEditing);
  const [showDownloadRouteModal, setShowDownloadRouteModal] = useState(false);
  const [showCreatedMapModal, toggleCreatedMapModal] = useState(false);
  const [submittingState, updateSubmittingState] = useState({ fromSubForm: false, isSubmitting: false, isNew: initialIsEditing });
  const [routingState, dispatchRoutes] = useRoutes({ initialRoutes: map.routes, messagingChannel, routeInfo, canEdit });
  const [waypointsState, dispatchWaypoints] = useWaypoints({ initialWaypoints: map.waypoints, messagingChannel, canEdit });
  const prevStateRef = useRef({ isEditing, showCreatedMapModal, submittingState, routingState, waypointsState, map });
  const isCreateMapView = new URLSearchParams(window.location.search).has('save');

  const initialAccordionStates = {
    description: !initialIsEditing,
    waypoints: map.waypoints.length > 0,
    photos: map.mapPhotos.length > 0,
    linkedTrail: false
  };

  const description = map?.description_source || map?.description;

  const defaultContentPrivacy = usersContentPrivacyPolicy(user, 'customMaps');

  const setInitialContentPrivacy = () => {
    if (map && map.contentPrivacy) {
      return map.contentPrivacy;
    }

    return defaultContentPrivacy || VISIBILITY_PUBLIC;
  };

  const initialDescriptionState = {
    title: map ? map.name : '',
    description,
    activity: findByValue(map.activity?.uid),
    date: map.created_at ? new Date(map.created_at) : new Date(),
    isPrivate: map ? !!map.private : false,
    contentPrivacy: setInitialContentPrivacy()
  };

  const [descriptionChangeset, updateDescriptionChangeset] = useState(initialDescriptionState);
  const [previousDescriptionChangeset, updatePreviousDescriptionChangeset] = useState(initialDescriptionState); // Used for analytics
  const [accordionsOpened, updateAccordionState] = useState(initialAccordionStates);

  const beginEditing = useCallback(() => {
    updatePreviousDescriptionChangeset(descriptionChangeset);
    toggleEditing(true);
  }, [descriptionChangeset]);

  useEffect(() => {
    if (
      (prevStateRef.current.routingState.editIndex === null && routingState.editIndex !== null) ||
      (prevStateRef.current.waypointsState.editIndex === null && waypointsState.editIndex !== null)
    ) {
      beginEditing();
    }
  }, [routingState.editIndex, waypointsState.editIndex, beginEditing]);

  useEffect(() => {
    // keeps previous state tracked
    prevStateRef.current = { map: null, isEditing, showCreatedMapModal, submittingState, routingState, waypointsState };
  });

  const handleFindLocationSelect = useCallback(feature => {
    const { latitude, longitude, placeName } = feature;

    // triggers showPopupAtLatLong in TrailPlannerMixin to update map lat/lng location and show popup
    messagingChannel.publish({
      topic: 'edit.show_popup_at_lat_lng',
      data: [latitude, longitude, placeName, 14]
    });
  }, []);

  const updateMap = useCallback(() => {
    if (map && map.id) {
      handleMapUpdate({ id: map.id });
    }
  }, [map]);

  const closeCreatedMapModal = () => {
    toggleCreatedMapModal(false);
  };

  // ROUTE PERSISTENCE
  if (isEditing || isCreateMapView) {
    handleSuccessfulMapSave = (responseMap: any) => {
      if (prevStateRef.current.submittingState.isSubmitting && !prevStateRef.current.submittingState.fromSubForm) {
        toggleEditing(false);
        const url = `/explore/map/${responseMap.slug}`;
        window.history.pushState(
          { page: PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE, selectedObjectId: responseMap.ID, filters: null },
          `Explore ${responseMap.name} | AllTrails`,
          url
        );
      }
      // update the description changeset -- needed because titles created automatically are not reflected in changeset
      if (responseMap.name !== descriptionChangeset.title) {
        updateDescriptionChangeset({ ...descriptionChangeset, title: responseMap.name });
      }
      if (prevStateRef.current.routingState.editParams) {
        dispatchRoutes({ type: CANCEL_ROUTE_FORM });
      }
      if (prevStateRef.current.waypointsState.editParams) {
        dispatchWaypoints({ type: CANCEL_WAYPOINT_FORM });
      }
      if (prevStateRef.current.submittingState.isNew && !prevStateRef.current.submittingState.fromSubForm) {
        toggleCreatedMapModal(true);
        // update so modal doesn't pop up if a user saves twice
        updateSubmittingState(prevState => ({ ...prevState, isNew: false }));
      }

      dispatchRoutes({ type: UPDATE_FOR_INCOMING_ROUTES, payload: responseMap.routes });
      dispatchWaypoints({ type: UPDATE_FOR_INCOMING_WAYPOINTS, payload: responseMap.waypoints });
      updateSubmittingState(prevState => ({ ...prevState, fromSubForm: false, isSubmitting: false }));
    };

    saveMap = (additionalParams = {}) => {
      // This saves map changes via search_app
      saveMapChanges({ descriptionChangeset, ...additionalParams })
        .then(handleSuccessfulMapSave)
        .catch(() => {
          updateSubmittingState(prevState => ({ ...prevState, fromSubForm: false, isSubmitting: false }));
        });
    };

    handleRouteSuccess = (newMap: any) => {
      dispatchRoutes({ type: CANCEL_ROUTE_FORM });
      dispatchRoutes({ type: UPDATE_FOR_INCOMING_ROUTES, payload: newMap.routes });
      updateProgressDialog('saved');
      updateExploreMap(newMap);
      updateSubmittingState(prevState => ({ ...prevState, isSubmitting: false }));
    };

    handleMapError = () => {
      updateProgressDialog('error');
      updateSubmittingState(prevState => ({ ...prevState, fromSubForm: false, isSubmitting: false }));
    };

    handleRouteSave = (e: any = null) => {
      if (e) {
        e.preventDefault();
      }

      if (typeof map.id === 'undefined') {
        updateSubmittingState(prevState => ({ ...prevState, fromSubForm: true, isSubmitting: true }));
        saveMap();
        return;
      }

      if (!routingState.editParams) {
        return;
      }

      updateProgressDialog('saving');
      updateSubmittingState(prevState => ({ ...prevState, isSubmitting: true }));

      routeRequest(map.id, routingState.editParams).then(handleRouteSuccess).catch(handleMapError);
    };

    const saveMapContentsThenMap = () => {
      // if there is an open waypoint or route form
      updateProgressDialog('saving');
      updateSubmittingState(prevState => ({ ...prevState, isSubmitting: true }));
      // if there are not route or waypoint editparams the promises will return null
      Promise.all([routeRequest(map.id, routingState.editParams), waypointRequest(map.id, waypointsState.editParams)])
        .then(() => {
          handleRouteInfoChanged(null);
          saveMap();
        })
        .catch(err => {
          handleMapError(err);
        });
    };

    saveAndDisplayMap = (e: any, fromSubForm = false) => {
      e.preventDefault();

      logIndividualContentPrivacyChangedAmplitudeEvent(
        'customMaps',
        map.id,
        defaultContentPrivacy,
        previousDescriptionChangeset.contentPrivacy,
        descriptionChangeset.contentPrivacy
      );

      updateSubmittingState(prevState => ({ ...prevState, fromSubForm, isSubmitting: true }));
      if (map.id && (routingState.editParams || waypointsState.editParams)) {
        saveMapContentsThenMap();
        return;
      }

      if (typeof map.id === 'undefined' && waypointsState.editParams) {
        const waypoint = formatWaypointForMap(waypointsState.editParams);
        createMapWithWaypoint(waypoint, false);
        return;
      }

      saveMap();
    };

    createMapWithWaypoint = (waypoint: any, fromSubForm = true) => {
      if (map.id) {
        return;
      }

      updateSubmittingState(prevState => ({ ...prevState, fromSubForm, isSubmitting: true }));
      const waypoints = [waypoint];
      saveMap({ waypoints });
    };
  }

  const toggleAccordionState = (accordionName: 'description' | 'waypoints' | 'photos' | 'linkedTrail') => {
    updateAccordionState(prevState => ({ ...prevState, [accordionName]: !prevState[accordionName] }));
  };

  const handleShareClick = () => {
    if (!user) {
      modalRoadblock('signup', 'explore-atmap', window.location.pathname, languageRegionCode);
      return;
    }

    if (showCreatedMapModal) {
      closeCreatedMapModal();
    }

    openShareModal({
      type: map.presentationType,
      ugc: true,
      id: map.id,
      slug: map.slug,
      name: map.name,
      primaryKey: 'id',
      private: map.private
    });
  };

  const handleDirectionsClick = () => {
    if (!user) {
      modalRoadblock('signup', 'explore-atmap', window.location.pathname, languageRegionCode);
      return;
    }

    const {
      location: { latitude, longitude }
    } = map;

    window.open(`https://www.google.com/maps/dir/Current+Location/${latitude},${longitude}`);
  };

  const handlePrintClick = () => {
    // fires handlePrintClick in search_map
    messagingChannel.publish({
      topic: 'print.navigate'
    });
  };

  const openDownloadModal = () => {
    if (!user) {
      modalRoadblock('signup', 'explore-atmap', window.location.pathname, languageRegionCode);
    }

    setShowDownloadRouteModal(true);
  };

  const toggleDownloadModal = () => {
    setShowDownloadRouteModal(!showDownloadRouteModal);
  };

  const handleDeleteSuccess = () => {
    window.location.href = wrapUrlSafe(`/members/${user.slug}/maps`, languageRegionCode);
  };

  const handleDeleteError = () => {
    updateProgressDialog('error-deleting-map');
  };

  const handleDeleteClick = () => {
    if (!window.confirm(DELETE_ITEM_QUESTION)) {
      return;
    }

    deleteMap(map.id)
      .then(resp => {
        if (resp.errors) {
          handleDeleteError();
          return;
        }

        handleDeleteSuccess();
      })
      .catch(() => {
        handleDeleteError();
      });
  };

  const closeDescriptionForm = () => {
    toggleAccordionState('description');
  };

  const renderHeader = () => {
    if (isEditing || isCreateMapView) {
      return <EditHeader />;
    }

    const { forcePhotoId } = resultCardFunctions;
    const { isFavorite, handleFavoriteClick } = listMethods;
    const bannerPhotoId = forcePhotoId(map.presentationType, map.ID);
    return (
      <ActionHeader
        data={map}
        isFavorite={isFavorite(map.presentationType, map.ID)}
        handleFavoriteClick={handleFavoriteClick}
        bannerPhotoId={bannerPhotoId}
        handleDirectionsClick={handleDirectionsClick}
        openDownloadModal={openDownloadModal}
        handleShareClick={handleShareClick}
        handleDeleteClick={handleDeleteClick}
        saveMapAsClone={() => saveMapAsClone(map)}
        type="map"
      />
    );
  };

  const renderLinkedTrail = () => {
    const { type, ID } = linkedTrail;

    const { allowClickingCompletedBadge, handleTrailClick, handleCompletedBadgeClick, handleFavoriteClick } = resultCardFunctions;

    const { isComplete, isFavorite, isVerified } = listMethods;

    const linkedTrailProps = {
      allowClickingCompletedBadge: allowClickingCompletedBadge(type, ID),
      handleCardClick: () => handleTrailClick(linkedTrail),
      handleCompletedBadgeClick: () => handleCompletedBadgeClick(type, ID),
      handleFavoriteClick: () => handleFavoriteClick({ type, id: ID }),
      isComplete: isComplete(type, ID),
      isFavorite: isFavorite(type, ID),
      isVerified: isVerified(type, ID),
      trail: linkedTrail,
      messagingChannel,
      key: `trail-${linkedTrail.ID}`
    };

    return <TrailCard {...linkedTrailProps} />;
  };

  return (
    <div className={`${styles.panel} ${hidePanel ? styles.hidePanel : ''}`}>
      {renderHeader()}
      <RouteSection
        canEdit={canEdit}
        isEditing={isEditing || isCreateMapView}
        routes={routingState.routes}
        sortMode={routingState.sortMode}
        dispatchRoutes={dispatchRoutes}
        dispatchWaypoints={dispatchWaypoints}
        editIndex={routingState.editIndex}
        editParams={routingState.editParams}
        messagingChannel={messagingChannel}
        summaryStats={map.summaryStats}
        handleFindLocationSelect={handleFindLocationSelect}
        mapboxAccessToken={mapboxAccessToken}
        mapId={map.id}
        updateMap={updateMap}
        openTrailFormModal={openTrailFormModal}
        handleRouteSave={handleRouteSave}
        isSubmitting={submittingState.isSubmitting}
        updateExploreMap={updateExploreMap}
        hidePanel={showMapMobile}
      />
      <div className={styles.accordionContainer}>
        {(isEditing || isCreateMapView) && (
          <Expandable
            title={DESCRIPTION}
            externalControls={{ toggleIsOpen: () => toggleAccordionState('description'), isOpen: accordionsOpened.description }}
          >
            <DescriptionForm
              initialChangeset={initialDescriptionState}
              changeset={descriptionChangeset}
              updateChangeset={updateDescriptionChangeset}
              handleDescriptionSave={saveAndDisplayMap}
              closeDescriptionForm={closeDescriptionForm}
            />
          </Expandable>
        )}
        <Expandable title={WAYPOINTS}>
          <WaypointsSection
            {...waypointsState}
            dispatchFormState
            dispatchWaypoints={dispatchWaypoints}
            messagingChannel={messagingChannel}
            mapId={map.id}
            canEdit={canEdit}
            createMapWithWaypoint={createMapWithWaypoint}
          />
        </Expandable>
        {map && map.id && (
          <Expandable contentClassName={styles.accordionExtended} hideBorder={!linkedTrail} title={PHOTOS}>
            <PhotoSection
              canEdit={canEdit}
              mapId={map.id}
              mapPhotos={map.mapPhotos}
              updateMapPhotos={updateMapPhotos}
              openTrailFormModal={openTrailFormModal}
              isEditing={isEditing || isCreateMapView}
            />
          </Expandable>
        )}
        {linkedTrail && (
          <Expandable hideBorder title={<FormattedMessage defaultMessage="Linked trail" />}>
            {renderLinkedTrail()}
          </Expandable>
        )}
      </div>
      <PortalModal isOpen={user && showDownloadRouteModal}>
        <DownloadRouteModal toggleDownloadModal={toggleDownloadModal} mapId={map.id} />
      </PortalModal>
      <div className={styles.panelSpacer} />
      {canEdit ? (
        <div className={styles.panelBottom}>
          {isEditing || isCreateMapView ? (
            <Button
              text={SAVE_CUSTOM_MAP}
              // @ts-ignore button type is missing event
              onClick={(e: MouseEvent) => {
                saveAndDisplayMap(e);
              }}
              disabled={submittingState.isSubmitting}
              testId="map-creator-save-custom-map"
              variant="primary"
              width="full"
            />
          ) : (
            <Button text={EDIT_MAP} onClick={beginEditing} testId="map-creator-edit-map" variant="default" width="full" />
          )}
        </div>
      ) : (
        <div className={styles.panelJib} />
      )}
      {showCreatedMapModal && (
        <CreatedMapModal
          closeModal={closeCreatedMapModal}
          handleShareClick={handleShareClick}
          handleDirectionsClick={handleDirectionsClick}
          handleDownloadRoute={openDownloadModal}
          handlePrintClick={handlePrintClick}
        />
      )}
    </div>
  );
};

MapCreatorPanel.defaultProps = {
  map: {},
  handleMapUpdate: () => {},
  openTrailFormModal: () => {},
  messagingChannel: {},
  mapboxAccessToken: '',
  resultCardFunctions: {}
};

export default MapCreatorPanel;
